import React, { useEffect, useMemo, useState } from 'react';
import FormPage from '../../shared/Layout/FormPage';
import CustomCard, { CustomGridLayout } from '../../shared/Cards/CustomCard';
import { CustomButton } from '../../shared/Button/CustomButton';
import { CustomAsyncReactSelect, CustomCalenderInput, CustomDropDown, CustomInput, CustomTextArea } from '../../shared/Input/AllInputs';
import useEmployees from '../../hooks/Employees/useEmployees';
import { eventStatusOptions, generateSequence, memberStatusOptions } from '../../utils/dropdownConstants';
import AddMember from './AddMember';
import { deleteEvent, deleteEventMember, editCalendarEventMember, getCalendarBooking } from '../../redux/actions/Calendar/CalendarAction';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { confirmDelete, convertToDateTime, getTime } from '../../utils/commonFunctions';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import _ from 'lodash';
import moment from 'moment';
import MemberVerification from './MemberVerification';
import { showToast } from '../../redux/actions/toastAction';
import EmployeeVerification from './EmployeeVerification';
import UpgradeDowngradeEvent from './UpgradeDwongradeEvent';
import CustomLayoutPlan from '../../shared/LayoutPlan/CustomLayoutPlan';

const ManageEvents = () => {
    const { id } = useParams();
    const dispatch = useDispatch();

    useEffect(() => {
        if (id) {
            dispatch(getCalendarBooking(id));
        }
    }, [id, dispatch]);

    const history = useHistory();

    const { calendarEvent } = useSelector((state) => state.calendar);

    useEffect(() => {
        if (calendarEvent && Object.keys(calendarEvent).length > 0) {
            setData((prevData) => ({
                ...prevData,
                staff: calendarEvent.staff?._id || null,
                eventDate: calendarEvent.eventDate ? new Date(calendarEvent.eventDate) : null,
                startTime: calendarEvent.startTime ? convertToDateTime(calendarEvent.startTime) : null,
                duration: Number(calendarEvent.duration) || null,
                member: calendarEvent.member || null,
                event: calendarEvent.event || null,
                status: calendarEvent.status,
                isStaffVerified: calendarEvent?.isStaffVerified,
                enrollment: `${calendarEvent?.member?.length}/${calendarEvent?.event?.maximumWaitlist}`,
            }));
        }
    }, [calendarEvent]);

    const { employees } = useEmployees();
    const suggestions = useMemo(
        () =>
            employees
                ?.filter((item) => Array.isArray(item.isClassLevel) && item.isClassLevel.length > 0)
                ?.map((item) => ({
                    value: item._id,
                    name: `${item.firstName} ${item.middleInitial} ${item.lastName}`,
                })),
        [employees],
    );

    const employeeOptions = useMemo(
        () =>
            employees
                ?.filter((item) => Array.isArray(item.isClassLevel) && item.isClassLevel.length > 0)
                ?.map((item) => ({ name: `${item.firstName} ${item.middleInitial} ${item.lastName}`, value: item?._id })),
        [employees],
    );

    const durationOptions = generateSequence();
    const [data, setData] = useState({
        staff: '',
        eventDate: '',
        startTime: '',
        duration: '',
        enrollment: ``,
        member: [],
        services: [],
        employeelevelService: [],
        memberService: [],
        memberStatus: [],
        isStaffVerified: false,
    });

    useEffect(() => {
        if (data?.staff) {
            const staff = employees.find((item) => item._id === data.staff);
            const level = staff?.isClassLevel?.[0];
            let eventService = data.event?.eventService?.find((item) => item.eventLevel === level);
            const services = eventService?.services || [];
            setData((prev) => ({
                ...prev,
                employeelevelService: services,
            }));
        } else {
            setData((prev) => ({
                ...prev,
                employeelevelService: [],
            }));
        }
    }, [data.staff, data.event, employees]);

    const handleChange = ({ name, value }) => {
        setData((prev) => ({ ...prev, [name]: value }));

        let d;
        if (name === 'startTime') {
            d = getTime(value);
        } else if (name === 'eventDate') {
            d = moment(value).format('YYYY-MM-DD');
        } else {
            d = value;
        }

        if (name === 'status' && value === 'COMPLETED') {
            const change = onChangeEventStatus();
            if (!change) {
                dispatch(
                    editCalendarEventMember(id, _, { status: 'COMPLETED' }, () => {
                        dispatch(getCalendarBooking(id));
                    }),
                );
            } else {
                setData((prev) => ({ ...prev, status: 'PENDING' }));
            }
            return;
        }

        dispatch(
            editCalendarEventMember(id, _, { [name]: d }, () => {
                dispatch(getCalendarBooking(id));
            }),
        );
    };
    const [memberAccessCodeOpen, setMemberAccessCodeOpen] = useState(false);
    const [employeeAccessCodeOpen, setEmployeeAccessCodeOpen] = useState(false);

    const handleChangeDynamicFields = (fieldName, index, event, memberId) => {
        const { name, value } = event.target;

        setData((prevData) => {
            const updatedField = Array.isArray(prevData[fieldName]) ? [...prevData[fieldName]] : [];
            const customIndex = index.rowIndex;
            updatedField[customIndex] = {
                ...updatedField[customIndex],
                [name]: value,
            };
            dispatch(
                editCalendarEventMember(id, _, { member: memberId, [name]: value }, () => {
                    dispatch(getCalendarBooking(id));
                }),
            );

            return {
                ...prevData,
                [fieldName]: updatedField,
            };
        });
    };

    const CustomServiceTemplate = (row, index) => {
        const idsToMatch = data?.employeelevelService || [];
        const filteredServices = row?.services?.filter((service) => idsToMatch.includes(service._id)) || [];
        const serviceUse = filteredServices?.find((item) => item.serviceUse);
        const defaultValue = serviceUse?._id || '';
        return data?.staff ? (
            filteredServices.length > 0 ? (
                <CustomDropDown
                    name="service"
                    value={data?.memberService?.[index.rowIndex]?.service || defaultValue}
                    onChange={(val) => handleChangeDynamicFields('memberService', index, val, row?._id)}
                    options={filteredServices.map((service) => ({
                        name: service.name,
                        value: service._id,
                    }))}
                    fieldName="services"
                    customIndex={index.rowIndex}
                    showLabel={false}
                    col={8}
                    placeholder="Select Service"
                />
            ) : (
                <div className="flex">
                    <div className="p-error">No Service Found</div>
                    <div className="cursor-pointer ml-2 text-blue-500 text-underline" onClick={() => history.push('/pos')}>
                        POS
                    </div>
                </div>
            )
        ) : (
            <div>Please Select Employee</div>
        );
    };

    let setupLayout = useMemo(() => calendarEvent?.location?.setupLayout, [calendarEvent]);
    let resourcesLayout = useMemo(() => calendarEvent?.location?.resourcesLayout || [], [calendarEvent]);

    let bookedSlots = useMemo(() => {
        let arr = [];
        data?.member.forEach(({ seat }) => {
            if (seat) {
                arr.push(seat);
            }
        });
        return arr;
    }, [data?.member]);

    console.log('bookedSlots==>', bookedSlots);

    let availableSlots = useMemo(
        () =>
            resourcesLayout
                .filter(({ shortName }) => !bookedSlots.includes(shortName))
                .map((item) => ({
                    name: item.shortName,
                    value: item.shortName,
                })),
        [resourcesLayout, bookedSlots],
    );

    const CustomSeatTemplate = (row, index) => {
        let _slots = availableSlots;
        // if (row?.seat) {
        //     _slots.push({ name: row?.seat, value: row?.seat });
        // }
        return (
            <CustomDropDown
                name="seat"
                data={row}
                onChange={(val) => handleChangeDynamicFields('memberService', index, val, row.member)}
                options={_slots}
                showLabel={false}
                fieldName="seat"
                placeholder={row?.seat}
                customIndex={index.rowIndex}
            />
        );
    };

    const onChangeEventStatus = () => {
        const activeConditions = data?.event?.requiredtoComplete ?? {};
        const conditions = [
            { key: 'employee', check: !data.staff, message: 'Add Employee to complete this event' },
            { key: 'member', check: !data.member || data.member.length === 0, message: 'Add Member to complete this event' },
            {
                key: 'memberVerification',
                check: data?.member?.some((member) => !member.isMemberVerified),
                message: 'Add Member Verification to complete this event',
            },
            {
                key: 'employeeVerification',
                check: data?.staff && !data?.isStaffVerified,
                message: 'Add Employee Verification to complete this event',
            },
        ];
        if (activeConditions['employeeVerification'] && !data?.isStaffVerified) {
            setEmployeeAccessCodeOpen(data?.staff);
        }
        for (const { key, check, message } of conditions) {
            if (activeConditions[key] && check) {
                dispatch(showToast({ severity: 'warn', summary: message }));
                setData((prev) => ({ ...prev, status: 'PENDING' }));
                return true;
            }
        }

        return false;
    };
    const CustomStatusTemplate = (row, index) => {
        return (
            <>
                <CustomDropDown
                    name="status"
                    data={row}
                    onChange={(val) => handleChangeDynamicFields('memberStatus', index, val, row.member)}
                    fieldName="status"
                    customIndex={index.rowIndex}
                    showLabel={false}
                    options={memberStatusOptions}
                    col={8}
                />
            </>
        );
    };

    const onDelete = (col, position) => {
        confirmDelete(
            () => {
                dispatch(
                    deleteEventMember(id, col.member, () => {
                        dispatch(getCalendarBooking(id));
                    }),
                );
            },
            'Do you want to delete this Member ?',
            position,
        );
    };

    const ActionTemplate = (r) => {
        return <i className="mx-2 cursor-pointer pi pi-trash" onClick={() => onDelete(r)}></i>;
    };

    const VerifyTemplate = (r) => {
        return r?.isMemberVerified ? (
            <i className="mx-2 cursor-pointer pi pi-check-circle text-green-500"></i>
        ) : (
            <i className="mx-2 cursor-pointer pi pi-times-circle text-red-500" onClick={() => setMemberAccessCodeOpen(r?.member)}></i>
        );
    };

    const [openMemberList, setOpenMemberList] = useState(false);
    const [isUpDownModal, setIsUpDownModal] = useState(false);

    const handleRemove = (position) => {
        confirmDelete(
            () => {
                dispatch(
                    deleteEvent(id, () => {
                        history.goBack();
                    }),
                );
            },
            'Do you want to delete this Event ?',
            position,
        );
    };

    const onAddMember = () => {
        if (data?.member.length >= calendarEvent?.event?.maximumWaitlist) {
            dispatch(showToast({ severity: 'error', summary: 'You cannot add more members than the maximum waitlist limit.' }));
        } else {
            setOpenMemberList(true);
        }
    };

    const onRepeatEvent = () => history.push(`/calender/events/${id}/repeat-event`);
    const onUpDownGradeEvent = () => setIsUpDownModal(true);

    console.log('data?.member==>', data?.member);

    return (
        <FormPage backText="Calendar">
            <div className="bg-lightest-blue  border-round-xl shadow-3  flex px-3 mb-2 mx-2 gap-2">
                <CustomButton onClick={handleRemove}>Remove Event</CustomButton>
                <CustomButton onClick={onAddMember}>Add Member</CustomButton>
                <CustomButton onClick={onRepeatEvent}>Repeat Event</CustomButton>
                <CustomButton onClick={onUpDownGradeEvent}>Upgrade/Downgrade Event</CustomButton>
                <AddMember
                    openMemberList={openMemberList}
                    setOpenMemberList={setOpenMemberList}
                    member={data?.member}
                    maximumWaitlist={Number(calendarEvent?.event?.maximumWaitlist) - data?.member?.length}
                />
                <UpgradeDowngradeEvent isOpen={isUpDownModal} setIsOpen={setIsUpDownModal} event={data?.event?._id || null} />
            </div>
            <CustomCard col="12" title="Event Details">
                <CustomGridLayout>
                    <CustomAsyncReactSelect
                        name="staff"
                        label="Employee"
                        suggestions={suggestions}
                        options={employeeOptions}
                        value={data.staff}
                        onChange={handleChange}
                        isClearable={false}
                        col={4}
                    />

                    <CustomCalenderInput name="eventDate" onChange={handleChange} data={data} col={4} />
                    <CustomCalenderInput name="startTime" onChange={handleChange} data={data} col={4} timeOnly hourformat={12} />
                    <CustomDropDown name="duration" options={durationOptions} onChange={handleChange} data={data} col={4} />
                    <CustomInput name="enrollment" data={data} disabled />
                    <CustomDropDown name="status" options={eventStatusOptions} onChange={handleChange} data={data} col={4} />
                    <CustomTextArea name="statusReason" data={data} col={8} />
                </CustomGridLayout>
            </CustomCard>
            <h2 className="text-semibold text-xl ml-1 my-2">Manage Event</h2>
            <DataTable value={data?.member} scrollable scrollHeight="400px" tableStyle={{ minWidth: '50rem' }}>
                <Column
                    field="firstName"
                    body={(r) => r?.firstName + '' + r?.lastName}
                    header="Member"
                    className="bg-light-green font-bold"
                    style={{ width: '20%' }}
                />
                <Column
                    className="bg-light-green font-bold"
                    header="Status"
                    body={(rowData, index) => CustomStatusTemplate(rowData, index)}
                    style={{ width: '25%' }}
                />
                <Column
                    className="bg-light-green font-bold"
                    header="Service"
                    body={(rowData, index) => CustomServiceTemplate(rowData, index)}
                    style={{ width: '25%' }}
                />
                {setupLayout && (
                    <Column
                        className="bg-light-green font-bold"
                        header="Seat"
                        body={(rowData, index) => CustomSeatTemplate(rowData, index)}
                        style={{ width: '25%' }}
                    />
                )}
                <Column header="Verified" className="bg-light-green font-bold" body={VerifyTemplate} style={{ width: '15%' }} />
                <Column header="Action" className="bg-light-green font-bold" body={ActionTemplate} style={{ width: '15%' }} />
            </DataTable>
            {setupLayout && (
                <>
                    <h2 className="text-semibold text-xl ml-1 mt-2">{calendarEvent?.location?.name} Layout</h2>
                    <CustomLayoutPlan name="selectSlots" value={calendarEvent?.location?.resourcesLayout} restrict booked={bookedSlots} />
                </>
            )}

            <MemberVerification memberAccessCodeOpen={memberAccessCodeOpen} setMemberAccessCodeOpen={setMemberAccessCodeOpen} id={id} />
            <EmployeeVerification employeeAccessCodeOpen={employeeAccessCodeOpen} setEmployeeAccessCodeOpen={setEmployeeAccessCodeOpen} id={id} />
        </FormPage>
    );
};

export default ManageEvents;
